import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck, FileText, ListChecks, Eye, Layers, Lock, ArrowRight,
  AlertTriangle, Building2, CheckCircle2, Globe2,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import HussarTopNav from '../components/HussarTopNav';
import useRouteMeta from '../hooks/useRouteMeta';

/**
 * Public doctrine page — Protokół AI / AI Protocol.
 *
 * Strictly a brand/category page: positions HussarAi as a European
 * protocol for human-controlled AI work. NO backend logic, NO admin
 * exposure, NO certification claims.
 *
 * Mounted at:
 *   /protokol-ai    (canonical PL)
 *   /ai-protocol   (EN alias)
 */

const LAYER_ICONS = [FileText, ListChecks, Eye, Layers, Lock];

const APPLIES_ICONS = [Building2, ShieldCheck, ShieldCheck, Globe2, ArrowRight];

const ProtokolAi = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'pl').startsWith('en') ? 'en' : 'pl';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  // Canonical = PL slug so search engines coalesce the alias under it.
  const pageUrl = `${origin}/protokol-ai`;
  const imageUrl = `${origin}/og/protokol-ai-og.png`;

  useRouteMeta({
    marker: 'protokol-ai',
    title: t('protocol.metaTitle'),
    description: t('protocol.metaDescription'),
    canonical: pageUrl,
    locale: lang === 'en' ? 'en_GB' : 'pl_PL',
    ogImage: imageUrl,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogImageAlt: t('protocol.h1'),
    ogUrl: pageUrl,
  });

  const layers = t('protocol.layers.items', { returnObjects: true }) || [];
  const applies = t('protocol.appliesTo.items', { returnObjects: true }) || [];
  const notDoing = t('protocol.notDoing.items', { returnObjects: true }) || [];

  return (
    <div className="hussar-landing" data-testid="protokol-ai-page">
      <HussarTopNav active="protocol" />

      {/* Hero */}
      <main className="hussar-agenci-main">
        <section className="hussar-agenci-hero" data-testid="protokol-ai-hero">
          <div className="hussar-agenci-eyebrow">
            <ShieldCheck className="w-3 h-3 inline-block mr-1" />
            {t('protocol.eyebrow')}
          </div>
          <h1 className="hussar-agenci-h1" data-testid="protokol-ai-h1">
            {t('protocol.h1')}
          </h1>
          <p className="hussar-agenci-subhead" data-testid="protokol-ai-subhead">
            {t('protocol.subhead')}
          </p>
          <p
            className="hussar-agenci-proof hussar-proof-strong"
            data-testid="protokol-ai-doctrine-badge"
          >
            {t('protocol.doctrineBadge')}
          </p>
        </section>

        {/* Positioning + expanded doctrine */}
        <section className="hussar-pol-op-section" data-testid="protokol-ai-positioning">
          <p
            className="hussar-pol-op-body"
            style={{ fontSize: '20px', fontWeight: 600 }}
            data-testid="protokol-ai-positioning-line"
          >
            {t('protocol.positioning')}
          </p>
          <p
            className="hussar-pol-op-body"
            style={{ marginTop: '12px', fontStyle: 'italic' }}
            data-testid="protokol-ai-doctrine-expanded"
          >
            {t('protocol.doctrineExpanded')}
          </p>
        </section>

        {/* What is the protocol */}
        <section className="hussar-pol-op-section" data-testid="protokol-ai-whatis">
          <h2 className="hussar-agenci-h2">{t('protocol.whatIs.title')}</h2>
          <p className="hussar-pol-op-body">{t('protocol.whatIs.body')}</p>
        </section>

        {/* Five control layers */}
        <section className="hussar-pol-op-section" data-testid="protokol-ai-layers">
          <h2 className="hussar-agenci-h2">{t('protocol.layers.title')}</h2>
          <div className="hussar-agenci-deliverables-grid">
            {layers.map((layer, i) => {
              const Icon = LAYER_ICONS[i] || ShieldCheck;
              return (
                <Card
                  key={layer.title}
                  className="hussar-agenci-deliverable-card"
                  data-testid={`protokol-ai-layer-${i}`}
                >
                  <div className="hussar-agenci-deliverable-icon">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="hussar-agenci-deliverable-title" style={{ fontSize: '15px' }}>
                    {layer.title}
                  </h3>
                  <p className="hussar-agenci-deliverable-body">{layer.body}</p>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Where the protocol applies */}
        <section className="hussar-pol-op-section" data-testid="protokol-ai-applies">
          <h2 className="hussar-agenci-h2">{t('protocol.appliesTo.title')}</h2>
          <div className="hussar-agenci-deliverables-grid">
            {applies.map((item, i) => {
              const Icon = APPLIES_ICONS[i] || Building2;
              const cardBody = (
                <>
                  <div className="hussar-agenci-deliverable-icon">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3
                    className="hussar-agenci-deliverable-title"
                    style={{
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>{item.label}</span>
                    {item.live && (
                      <span
                        className="hussar-protocol-live-pill"
                        data-testid={`protokol-ai-applies-${i}-live`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '2px 8px',
                          borderRadius: 999,
                          border: '1px solid rgba(125, 211, 252, 0.22)',
                          background: 'rgba(125, 211, 252, 0.06)',
                          color: '#7DD3FC',
                          fontSize: 10,
                          letterSpacing: 0.4,
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        {t('protocol.liveBadge')}
                      </span>
                    )}
                  </h3>
                  <p className="hussar-agenci-deliverable-body">{item.body}</p>
                </>
              );
              return item.to ? (
                <Link
                  key={item.label}
                  to={item.to}
                  className="hussar-agenci-deliverable-card hussar-agenci-deliverable-card-link"
                  data-testid={`protokol-ai-applies-${i}`}
                >
                  {cardBody}
                </Link>
              ) : (
                <Card
                  key={item.label}
                  className="hussar-agenci-deliverable-card"
                  data-testid={`protokol-ai-applies-${i}`}
                >
                  {cardBody}
                </Card>
              );
            })}
          </div>
        </section>

        {/* What the protocol does NOT do */}
        <section className="hussar-pol-op-section" data-testid="protokol-ai-notdoing">
          <h2 className="hussar-agenci-h2">
            <AlertTriangle className="w-6 h-6 inline-block mr-2" />
            {t('protocol.notDoing.title')}
          </h2>
          <ul className="hussar-pol-op-bullets">
            {notDoing.map((line, i) => (
              <li key={line} data-testid={`protokol-ai-notdoing-${i}`}>
                {line}
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section
          className="hussar-pol-op-section hussar-pol-op-footer-cta"
          data-testid="protokol-ai-cta"
        >
          <h2 className="hussar-agenci-h2">{t('protocol.cta.title')}</h2>
          <p className="hussar-pol-op-body">{t('protocol.cta.body')}</p>
          <div className="hussar-agenci-cta-row">
            <Link to={t('protocol.cta.to')} data-testid="protokol-ai-cta-link">
              <Button size="lg" className="hussar-btn-primary hussar-btn-large">
                <CheckCircle2 className="w-5 h-5" />
                {t('protocol.cta.button')}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>

        <footer className="hussar-pol-op-footer" data-testid="protokol-ai-footer">
          <p>{t('protocol.doctrineBadge')}</p>
          <p className="hussar-pol-op-footer-meta">{t('protocol.footerMeta')}</p>
        </footer>
      </main>
    </div>
  );
};

export default ProtokolAi;
